'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: string[];
  route: string;
}

const ServiceSelector = () => {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const services: Service[] = [
    {
      id: 'infrastructure',
      icon: 'ServerIcon',
      title: 'IT Infrastructure',
      description: 'Build and maintain robust, scalable technology foundations',
      features: ['Network Design', 'Server Management', 'Hardware Procurement', 'System Integration'],
      route: '/services-overview'
    },
    {
      id: 'cybersecurity',
      icon: 'ShieldCheckIcon',
      title: 'Cybersecurity',
      description: 'Protect your business from evolving digital threats',
      features: ['Threat Detection', 'Security Audits', 'Compliance Management', 'Incident Response'],
      route: '/services-overview'
    },
    {
      id: 'cloud',
      icon: 'CloudIcon',
      title: 'Cloud Solutions',
      description: 'Migrate, optimize, and manage your cloud infrastructure',
      features: ['Cloud Migration', 'Multi-Cloud Strategy', 'Cost Optimization', 'Disaster Recovery'],
      route: '/services-overview'
    },
    {
      id: 'managed',
      icon: 'CogIcon',
      title: 'Managed Services',
      description: 'Proactive IT management and 24/7 monitoring',
      features: ['24/7 Monitoring', 'Patch Management', 'Help Desk Support', 'Performance Optimization'],
      route: '/services-overview'
    },
    {
      id: 'consulting',
      icon: 'LightBulbIcon',
      title: 'IT Consulting',
      description: 'Strategic technology guidance for business transformation',
      features: ['Digital Strategy', 'Technology Roadmap', 'Vendor Selection', 'Process Optimization'],
      route: '/services-overview'
    },
    {
      id: 'low-current',
      icon: 'CircuitBoardIcon',
      title: 'Low Current Systems',
      description: 'Professional cabling and infrastructure solutions',
      features: ['Structured Cabling', 'Network Infrastructure', 'Audio-Visual Systems', 'Building Automation'],
      route: '/services-overview'
    },
    {
      id: 'biometric',
      icon: 'FingerPrintIcon',
      title: 'Biometric Security',
      description: 'Advanced facial recognition and access control',
      features: ['Facial Recognition', 'Fingerprint Scanners', 'Access Control', 'Time Tracking'],
      route: '/services-overview'
    },
    {
      id: 'support',
      icon: 'LifebuoyIcon',
      title: 'Emergency Support',
      description: 'Rapid response for critical IT issues',
      features: ['15-Min Response', 'Remote Assistance', 'On-Site Support', 'Crisis Management'],
      route: '/contact'
    }
  ];

  return (
    <section className="py-20 bg-muted">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            Find Your Perfect IT Solution
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Select a service to explore how we can help your business thrive with technology
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              onClick={() => setSelectedService(service.id === selectedService ? null : service.id)}
              className={`bg-card rounded-xl p-6 cursor-pointer transition-all duration-300 border-2 ${
                selectedService === service.id
                  ? 'border-accent shadow-elevated scale-105'
                  : 'border-border hover:border-accent/50 hover:shadow-card'
              }`}
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                  selectedService === service.id ? 'bg-accent' : 'bg-accent/10'
                }`}>
                  <Icon
                    name={service.icon as any}
                    size={28}
                    variant="outline"
                    className={selectedService === service.id ? 'text-white' : 'text-accent'}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-headline font-bold text-xl text-primary mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-body">
                    {service.description}
                  </p>
                </div>
              </div>

              {selectedService === service.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <p className="text-sm font-cta font-semibold text-primary mb-2">Key Features:</p>
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                      <span className="text-sm text-foreground font-body">{feature}</span>
                    </div>
                  ))}
                  <a
                    href={service.route}
                    className="flex items-center justify-center space-x-2 w-full mt-4 px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg font-cta font-semibold text-sm transition-colors duration-200"
                  >
                    <span>Learn More</span>
                    <Icon name="ArrowRightIcon" size={16} variant="outline" />
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceSelector;